/**
 * <video-hover-preview>: poster + desktop hover preview + click to play.
 * Self-hosted: muted video on hover. External (YouTube/Vimeo): Ken Burns on poster.
 */
const VideoHoverPreviewRegistry = {
  instances: new Set(),
  active: null,

  register(instance) {
    this.instances.add(instance);
  },

  unregister(instance) {
    this.instances.delete(instance);
    if (this.active?.instance === instance) {
      this.active = null;
    }
  },

  claim(instance, mode) {
    this.instances.forEach((other) => {
      if (other === instance) return;
      if (mode === 'preview' && other.classList.contains('is-playing')) return;
      other.resetPlayback({ releaseMedia: mode === 'playing' });
    });

    this.active = { instance, mode };

    if (mode === 'playing') {
      this.pauseOtherMedia(instance);
    }
  },

  pauseOtherMedia(activeInstance) {
    document.querySelectorAll('.js-youtube').forEach((video) => {
      if (activeInstance.contains(video)) return;
      video.contentWindow.postMessage(
        '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
        '*'
      );
    });
    document.querySelectorAll('.js-vimeo').forEach((video) => {
      if (activeInstance.contains(video)) return;
      video.contentWindow.postMessage('{"method":"pause"}', '*');
    });
    document.querySelectorAll('video').forEach((video) => {
      if (!activeInstance.contains(video)) video.pause();
    });
    document.querySelectorAll('product-model').forEach((model) => {
      if (model.modelViewerUI) model.modelViewerUI.pause();
    });
  },

  release(instance) {
    if (this.active?.instance === instance) {
      this.active = null;
    }
  },

  isActive(instance) {
    return this.active?.instance === instance;
  },
};

const HOVER_DEBOUNCE_MS = 175;

class VideoHoverPreview extends HTMLElement {
  constructor() {
    super();
    this.onPlayClick = this.onPlayClick.bind(this);
    this.onPointerEnter = this.onPointerEnter.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onIntersection = this.onIntersection.bind(this);
  }

  connectedCallback() {
    this.frame = this.querySelector('.video-hover-preview__frame');
    this.mediaMount = this.querySelector('.video-hover-preview__media-mount');
    this.playButton = this.querySelector('.video-hover-preview__play');
    this.isExternal = this.dataset.external === 'true';
    this.hoverPreviewEnabled =
      this.dataset.hoverPreview === 'true' &&
      window.matchMedia('(hover: hover)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.inView = false;
    this.observer = new IntersectionObserver(this.onIntersection, {
      rootMargin: '80px',
      threshold: 0.15,
    });
    if (this.frame) this.observer.observe(this.frame);

    this.playButton?.addEventListener('click', this.onPlayClick);

    if (this.hoverPreviewEnabled && this.frame) {
      this.frame.addEventListener('pointerenter', this.onPointerEnter);
      this.frame.addEventListener('pointerleave', this.onPointerLeave);
    }

    VideoHoverPreviewRegistry.register(this);
  }

  disconnectedCallback() {
    this.cancelHoverDebounce();
    this.resetPlayback({ releaseMedia: true });
    VideoHoverPreviewRegistry.release(this);
    VideoHoverPreviewRegistry.unregister(this);

    this.playButton?.removeEventListener('click', this.onPlayClick);
    if (this.hoverPreviewEnabled && this.frame) {
      this.frame.removeEventListener('pointerenter', this.onPointerEnter);
      this.frame.removeEventListener('pointerleave', this.onPointerLeave);
    }
    this.observer?.disconnect();
  }

  onIntersection(entries) {
    this.inView = entries.some((entry) => entry.isIntersecting);
    if (!this.inView) {
      this.resetPlayback({ releaseMedia: this.classList.contains('is-playing') });
      VideoHoverPreviewRegistry.release(this);
    }
  }

  getVideo() {
    return this.mediaMount?.querySelector('video');
  }

  getIframe() {
    return this.mediaMount?.querySelector('iframe');
  }

  getTemplate() {
    if (this.isExternal) {
      return this.querySelector('template[data-template="play"]');
    }
    return this.querySelector('template[data-template="video"]');
  }

  pauseIframe() {
    const iframe = this.getIframe();
    if (!iframe) return;

    if (iframe.classList.contains('js-youtube')) {
      iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    } else if (iframe.classList.contains('js-vimeo')) {
      iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
    }
  }

  ensureMedia() {
    if (this.hasAttribute('loaded-media')) {
      return this.getVideo() || this.getIframe();
    }

    const template = this.getTemplate();
    if (!template || !this.mediaMount) return null;

    const media = template.content.firstElementChild?.cloneNode(true);
    if (!media) return null;

    this.mediaMount.appendChild(media);
    this.mediaMount.hidden = false;
    this.setAttribute('loaded-media', '');

    this.mediaMount.querySelectorAll('video > img').forEach((img) => {
      img.setAttribute('alt', '');
      img.setAttribute('role', 'presentation');
    });

    return this.getVideo() || this.getIframe();
  }

  clearMediaMount() {
    if (!this.mediaMount) return;
    this.mediaMount.innerHTML = '';
    this.mediaMount.hidden = true;
    this.removeAttribute('loaded-media');
  }

  cancelHoverDebounce() {
    if (this._hoverDebounce) {
      clearTimeout(this._hoverDebounce);
      this._hoverDebounce = null;
    }
  }

  resetPlayback({ releaseMedia = false } = {}) {
    this.cancelReveal();
    this.cancelHoverDebounce();

    const video = this.getVideo();
    if (video) {
      video.pause();
      try {
        video.currentTime = 0;
      } catch (error) {
        /* ignore seek errors */
      }
      video.muted = true;
      video.loop = true;
      video.controls = false;
    }

    if (this.isExternal && this.getIframe()) {
      this.pauseIframe();
    }

    if (releaseMedia && this.hasAttribute('loaded-media')) {
      this.clearMediaMount();
    }

    this.classList.remove('is-previewing', 'is-playing');
  }

  onPointerEnter() {
    if (!this.inView || this.classList.contains('is-playing')) return;

    if (this.isExternal) {
      this.startPreview();
      return;
    }

    this.cancelHoverDebounce();
    this._hoverDebounce = setTimeout(() => {
      this._hoverDebounce = null;
      if (!this.inView || this.classList.contains('is-playing')) return;
      this.startPreview();
    }, HOVER_DEBOUNCE_MS);
  }

  onPointerLeave() {
    this.cancelHoverDebounce();
    if (this.classList.contains('is-playing')) return;
    if (!VideoHoverPreviewRegistry.isActive(this)) return;
    this.stopPreview();
    VideoHoverPreviewRegistry.release(this);
  }

  cancelReveal() {
    if (this._cancelReveal) {
      this._cancelReveal();
      this._cancelReveal = null;
    }
  }

  whenVideoFramesVisible(video, callback) {
    this.cancelReveal();

    const run = () => {
      if (typeof callback === 'function') callback();
      this._cancelReveal = null;
    };

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && !video.paused) {
      run();
      return;
    }

    const onPlaying = () => run();
    video.addEventListener('playing', onPlaying, { once: true });
    this._cancelReveal = () => video.removeEventListener('playing', onPlaying);
  }

  stopPreview() {
    this.cancelReveal();

    const video = this.getVideo();
    if (video) {
      video.pause();
      try {
        video.currentTime = 0;
      } catch (error) {
        /* ignore seek errors */
      }
    }

    this.classList.remove('is-previewing');
  }

  startPreview() {
    if (this.isExternal) {
      VideoHoverPreviewRegistry.claim(this, 'preview');
      this.classList.add('is-previewing');
      return;
    }

    const video = this.ensureMedia();
    if (!video || video.tagName !== 'VIDEO') return;

    VideoHoverPreviewRegistry.claim(this, 'preview');

    video.muted = true;
    video.loop = true;
    video.controls = false;
    video.setAttribute('playsinline', '');

    if (video.readyState < HTMLMediaElement.HAVE_METADATA) {
      video.load();
    }

    this.whenVideoFramesVisible(video, () => {
      if (!VideoHoverPreviewRegistry.isActive(this)) return;
      this.classList.add('is-previewing');
    });

    const playPromise = video.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {
        this.cancelReveal();
      });
    }
  }

  onPlayClick(event) {
    event.preventDefault();

    VideoHoverPreviewRegistry.claim(this, 'playing');
    this.cancelHoverDebounce();
    this.classList.remove('is-previewing');

    if (this.isExternal) {
      const media = this.ensureMedia();
      if (!media) {
        VideoHoverPreviewRegistry.release(this);
        return;
      }

      this.classList.add('is-playing');
      media.focus();
      return;
    }

    const media = this.ensureMedia();
    if (!media) {
      VideoHoverPreviewRegistry.release(this);
      return;
    }

    if (media.tagName === 'VIDEO') {
      media.pause();
      try {
        media.currentTime = 0;
      } catch (error) {
        /* ignore seek errors */
      }

      media.muted = false;
      media.loop = this.dataset.loopOnPlay === 'true';
      media.controls = true;
      media.setAttribute('playsinline', '');

      const showPlaying = () => {
        this.classList.add('is-playing');
      };

      this.whenVideoFramesVisible(media, showPlaying);

      const playPromise = media.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {
          this.cancelReveal();
          VideoHoverPreviewRegistry.release(this);
        });
      }
      media.focus();
    }
  }
}

if (!customElements.get('video-hover-preview')) {
  customElements.define('video-hover-preview', VideoHoverPreview);
}
